import { useState, useEffect } from 'react'
import { config } from '../config/environment'

interface AuthState {
  token: string | null
  email: string | null
  name: string | null
  userId: string | null
  ready: boolean
  error: string | null
}

/**
 * Resolve authentication for the Ros app.
 *
 * Three paths:
 * 1. Token in URL param (?token=...) — from Studio/Rival redirect
 * 2. Dev auth — local development with reachy-cf dev auth
 * 3. No token — show login prompt (future: standalone login page)
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    token: null,
    email: null,
    name: null,
    userId: null,
    ready: false,
    error: null,
  })

  useEffect(() => {
    resolveAuth().then(setState)
  }, [])

  return state
}

async function resolveAuth(): Promise<AuthState> {
  const params = new URLSearchParams(window.location.search)

  // Path 1: Token from URL (Studio/Rival redirect)
  const urlToken = params.get('token')
  if (urlToken) {
    // Remove token from URL to prevent leaking in browser history
    const cleanUrl = new URL(window.location.href)
    cleanUrl.searchParams.delete('token')
    window.history.replaceState({}, '', cleanUrl.toString())

    const user = parseJwtPayload(urlToken)
    return {
      token: urlToken,
      email: user?.email || null,
      name: user?.name || user?.given_name || null,
      userId: user?.sub || null,
      ready: true,
      error: null,
    }
  }

  // Path 2: Dev auth — local development AND deployed dev stacks (dev-<name>). The worker still
  // gates /auth/dev-login on its own ENVIRONMENT=development, so this only succeeds where dev-auth
  // is actually enabled server-side.
  if (config.ENVIRONMENT === 'development' || config.ENVIRONMENT.startsWith('dev-')) {
    // Use email from URL param or default to first dev user
    const devEmail = params.get('email') || 'rohan@rivaltech.com'
    try {
      // Hit the reachy-cf worker. Local: API_CF_URL is empty → relative path → Vite proxy. Deployed:
      // API_CF_URL is the worker URL (the ros-app pages.dev origin has no proxy, so it must be absolute).
      const res = await fetch(`${config.API_CF_URL}/auth/dev-login?email=${encodeURIComponent(devEmail)}&json=true`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json() as { token: string; user: { sub: string; email: string; name: string; groups: string[] } }
        return {
          token: data.token,
          email: data.user.email,
          name: data.user.name,
          userId: data.user.sub,
          ready: true,
          error: null,
        }
      }
    } catch {
      // Dev auth not available — fall through
    }

    // Fallback: no auth in dev mode (sessionId-based routing)
    return {
      token: null,
      email: devEmail,
      name: null,
      userId: 'dev-user',
      ready: true,
      error: null,
    }
  }

  // Path 3: No token, not dev — need login
  return {
    token: null,
    email: null,
    name: null,
    userId: null,
    ready: true,
    error: 'Authentication required. Please access Ros from Studio or the Rival platform.',
  }
}

function parseJwtPayload(token: string): Record<string, string> | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}
